import React from 'react';
import PropTypes from 'prop-types';
import {Map} from 'immutable';
import {GET} from './methods';
import Request from './Request';
import ResourcesManager from './ResourcesManager';
import {QUEUED} from './reducers';
import {PRODUCTION} from 'utils/env';
import camelcase from 'camelcase';
import * as actions from './actions';

const REQUESTS_STATE_KEY = '__@requests@__';

export const ANY = 'ANY';
export const IS_QUEUED = 'IS_QUEUED';
export const IS_NOT_FOUND = 'IS_NOT_FOUND';
export const IS_NOT_ALLOWED = 'IS_NOT_ALLOWED';
export const IS_DONE = 'IS_DONE';
export const HAS_NO_CONTENT = 'HAS_NO_CONTENT';
export const HAS_ERRORS = 'HAS_ERRORS';
export const HAS_FAILED = 'HAS_FAILED';

let cachedRequestsQueue = Map();

export function clearCachedRequestsQueue() {
  cachedRequestsQueue = Map();
}

export function splitCacheable(requests, queue) {
  const cached = {};
  const notCached = {};
  let nextQueue = queue;

  Object.keys(requests).forEach((key) => {
    const request = requests[key];
    const url = request.url.join('/');

    if (request.useCache && request.method === GET && nextQueue.has(url)) {
      cached[key] = nextQueue.get(url);
    } else {
      notCached[key] = request;

      if (request.method === GET) {
        nextQueue = nextQueue.set(url, request);
      }
    }
  });

  return {cached, notCached, nextQueue};
}

export default {
  propTypes: {
    requests: PropTypes.object.isRequired,
  },

  hasRequest(key) {
    return (typeof this.requests !== 'undefined') ? !!this.requests[key] : false;
  },

  componentWillMount() {
    this.requests = {};
  },

  componentDidMount() {
    this.callTransitions(this.props, this.props, true);
  },

  componentWillReceiveProps(nextProps) {
    this.uncacheFailures(this.props.requests);
    this.callTransitions(this.props, nextProps);
  },

  componentWillUnmount() {
    this.clearRequests();
  },

  uncacheFailures(state) {
    cachedRequestsQueue = cachedRequestsQueue.filter((request) => {
      const status = state.getIn(['status', request.uuid]);

      return typeof status === 'undefined' || status === QUEUED || (status >= 200 && status < 300);
    });
  },

  callTransitions(lhProps, rhProps, initial = false) {
    if (typeof this.transitions !== 'function') {
      return;
    }

    const lhRm = this.resourceManager(lhProps);
    const rhRm = this.resourceManager(rhProps);
    const transitions = [];
    const register = (transition) => transitions.push(transition);

    this.transitions(register);

    for (const transition of transitions) {
      const {from, to, request} = Object.assign({}, {from: ANY, to: ANY}, transition);
      const fromState = (from === ANY) ? true : lhRm.requests[camelcase(from)](request);
      const lastState = (to === ANY) ? true : lhRm.requests[camelcase(to)](request);
      const toState = (to === ANY) ? true : rhRm.requests[camelcase(to)](request);

      if (fromState && toState && (!lastState || initial)) {
        transition.call.bind(this)(rhRm);
      }
    }
  },

  resourceManager(nextProps = null) {
    if (!PRODUCTION && !this.props.requests) {
      /* eslint-disable no-console */
      console.error(
        `Trying to create resource manager but this.props.requests is ${typeof this.props.requests}`
      );
      /* eslint-enable no-console */
    }

    const props = nextProps || this.props;

    return ResourcesManager({
      requests: this.requests,
      representations: props.requests.get('representations'),
      resources: props.requests.get('resources'),
      status: props.requests.get('status'),
      errors: props.requests.get('errors'),
    });
  },

  setRequest(nextRequests, options = {}) {
    Object.keys(nextRequests).forEach((key) => {
      const maybeRequest = nextRequests[key];

      if (!(maybeRequest instanceof Request)) {
        throw new Error(`Cannot set request for ${key} because it's not a Request instance`);
      }
    });

    const nextOptions = Object.assign(
      {},
      {skipDispatch: false},
      options
    );
    const nextState = {};
    const {
      cached,
      notCached,
      nextQueue,
    } = splitCacheable(nextRequests, cachedRequestsQueue);

    cachedRequestsQueue = nextQueue;

    this.requests = nextState[REQUESTS_STATE_KEY] = Object.assign(
      {},
      (this.requests || {}),
      cached,
      notCached,
    );

    this.setState(nextState, () => {
      if (!this.props.dispatch) {
        throw new Error('No dispatch prop found on this component, cannot setRequest without it');
      }

      if (!nextOptions.skipDispatch) {
        this.props.dispatch(
          actions.queueRequests(Object.keys(notCached).map((i) => notCached[i]))
        );
      }
    });
  },

  clearRequests(requests) {
    const nextState = {};

    this.requests = nextState[REQUESTS_STATE_KEY] = {};

    this.setState(nextState);
  },

  deleteRequest(request) {
    const nextState = {};
    const requests = this.state[REQUESTS_STATE_KEY];

    const requestName = Object.keys(requests).find((key) => {
      return requests[key] === request;
    });

    delete requests[requestName];

    this.requests = nextState[REQUESTS_STATE_KEY] = requests;

    this.setState(nextState);
  },
};



// WEBPACK FOOTER //
// ./app/javascript/requests/RequestsMixin.js